import * as spriteIcons from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import SelectCategoriesModal from 'components/UI/Modal/SelectCategoriesModal/SelectCategoriesModal'
import ShareModal from 'components/UI/Modal/Share/ShareModal'
import { useModalContext } from 'context/modalContext'
import style from 'pages/vault/_vault.module.scss'
import { type ICategory } from 'types/interfaces/ITypes'

import { postIdOf, vaultIdOf } from '../vault.helpers'

type Props = {
  drawerOpen: boolean
  isGrouped: boolean
  onSwitchToGrouped: () => void

  activeCategory: any[]
  navigate: any

  allSelectedMedia: any[]
  selectedPosts: any[]
  postIds: string[]

  setSelecetedPosts: (v: any) => void
  setAllSelectedMedia: (v: any) => void
  setPostIds: (v: string[]) => void
  
  deleteMedia: () => void
  removeMedia: () => void
  addMediaToCategory: (payload: {
    id: string
    posts: string[]
    messages: string[]
    stories: string[]
  }) => void

  userCategories: ICategory[]
}

export function VaultDrawer(props: Props) {
  const {
    drawerOpen,
    isGrouped,
    onSwitchToGrouped,

    activeCategory,
    navigate,

    allSelectedMedia,
    selectedPosts,
    postIds,

    setSelecetedPosts,
    setAllSelectedMedia,
    setPostIds,

    deleteMedia,
    removeMedia,
    addMediaToCategory,

    userCategories,
  } = props

  const modalData = useModalContext()

  return (
    <div
      className={`${style.shared_media_drawer} ${
        drawerOpen ? style.shared_media_drawer_open : ''
      } ${!isGrouped ? style.shared_media_drawer_tall : ''}`}
    >
      {!isGrouped && (
        <div>
          Switch to{' '}
          <button
            type="button"
            onClick={e => {
              e.stopPropagation()
              onSwitchToGrouped()
            }}
            className={style.grouped_view_button}
          >
            Grouped View
          </button>{' '}
          to Categorize or Delete
        </div>
      )}

      <div className={style.shared_media_buttons_row}>
        {/* DELETE */}
        <div className={style.shared_media_button_container}>
          <div
            className={style.shared_media_drawer_button}
            role="button"
            aria-disabled={!isGrouped}
            tabIndex={isGrouped ? 0 : -1}
            style={
              !isGrouped
                ? {
                    opacity: 0.5,
                    pointerEvents: 'none',
                  }
                : undefined
            }
            onClick={() => {
              if (!isGrouped) return

              modalData.addModal(
                'Delete Media',
                <ConfirmModal
                  body="Are you sure you want to delete selected media?"
                  confirmFn={() => deleteMedia()}
                />
              )
            }}
          >
            <spriteIcons.IconTrashcanLarge color="#FCFCFC" />
          </div>

          <div className={style.shared_media_drawer_button_text}>
            Delete
          </div>
        </div>

        {/* SHARE */}
        <div className={style.shared_media_button_container}>
          <div
            className={style.shared_media_drawer_button}
            onClick={() => {
              modalData.addModal(
                '',
                <ShareModal
                  clickFns={{
                    newPost: () => {
                      localStorage.setItem(
                        'share-post',
                        JSON.stringify(allSelectedMedia)
                      )

                      modalData.clearModal()
                      navigate('/new/post/create')
                    },

                    newTabPost: () => {
                      localStorage.setItem(
                        'share-post',
                        JSON.stringify(allSelectedMedia)
                      )

                      modalData.clearModal()

                      const newWindow =
                        window.open('/new/post/create')

                      newWindow?.focus()
                    },

                    message: () => {
                      localStorage.setItem(
                        'share-message',
                        JSON.stringify(allSelectedMedia)
                      )

                      modalData.clearModal()
                      navigate('/messages/new')
                    },

                    story: () => {
                      localStorage.setItem(
                        'share-story',
                        JSON.stringify(allSelectedMedia)
                      )

                      modalData.clearModal()
                      navigate('/new/story')
                    },
                  }}
                  storyDisabled={allSelectedMedia.length > 1}
                />,
                false,
                true,
                style.modal_share
              )
            }}
          >
            <spriteIcons.IconShare />
          </div>

          <div className={style.shared_media_drawer_button_text}>
            Share
          </div>
        </div>

        {/* ADD TO */}
        <div className={style.shared_media_button_container}>
          <div
            className={style.shared_media_drawer_button}
            role="button"
            aria-disabled={!isGrouped}
            tabIndex={isGrouped ? 0 : -1}
            style={
              !isGrouped
                ? {
                    opacity: 0.5,
                    pointerEvents: 'none',
                  }
                : undefined
            }
            onClick={() => {
              if (!isGrouped) return

              modalData.addModal(
                'Add To Category',
                <SelectCategoriesModal
                  selectedCategories={[]}
                  onlyOne={true}
                  onCancel={() => modalData.clearModal()}
                  onSave={(val: any) => {
                    if (!val?.length) {
                      addToast(
                        'error',
                        'Select a media category'
                      )

                      return
                    }

                    const posts = postIds.filter(
                      (pid: string) =>
                        allSelectedMedia.some(
                          (item: any) =>
                            item.post &&
                            postIdOf(item) === pid
                        )
                    )

                  const messages: string[] = Array.from(
                    new Set(
                      allSelectedMedia
                        .filter(
                          (it: any) =>
                            it?.type === 'Message' ||
                            it?.message?.message
                        )
                        .map((it: any) => {
                          return (
                            it?.message?.message?.id ??
                            it?.message?.id ??
                            null
                          )
                        })
                        .filter(Boolean)
                    )
                  )

                    const stories = allSelectedMedia
                      .filter(
                        (item: any) =>
                          selectedPosts.includes(
                            item.id
                          ) && item.story?.id
                      )
                      .map(
                        (item: any) =>
                          item.story.id
                      )

                    addMediaToCategory({
                      id: val[0].id,
                      posts,
                      messages,
                      stories,
                    })

                    modalData.clearModal()
                    setSelecetedPosts([])
                    setAllSelectedMedia([])
                    setPostIds([])
                  }}
                  categories={
                    userCategories
                      .filter(
                        (item: any) =>
                          item.id !==
                          activeCategory?.[0]?.id
                      )
                      .map((item: any) => ({
                        ...item,
                        posts: 0,
                        premium: 0,
                        stories: 0,
                        messages: 0,
                        selected: false,
                      })) as ICategory[]
                  }
                />
              )
            }}
          >
            <spriteIcons.IconList color="#FCFCFC" />
          </div>

          <div className={style.shared_media_drawer_button_text}>
            Add to
          </div>
        </div>

        {/* REMOVE */}
        {activeCategory?.[0]?.id !== null && (
          <div
            className={
              style.shared_media_button_container
            }
          >
            <div
              className={
                style.shared_media_drawer_button
              }
              role="button"
              aria-disabled={!isGrouped}
              tabIndex={isGrouped ? 0 : -1}
              style={
                !isGrouped
                  ? {
                      opacity: 0.5,
                      pointerEvents: 'none',
                    }
                  : undefined
              }
              onClick={() => {
                if (!isGrouped) return

                modalData.addModal(
                  'Remove From Category',
                  <ConfirmModal
                    body="Are you sure you want to remove the selected media from this category?"
                    confirmFn={() => removeMedia()}
                  />
                )
              }}
            >
              <spriteIcons.IconList color="#FCFCFC" />
            </div>

            <div
              className={
                style.shared_media_drawer_button_text
              }
            >
              Remove
            </div>
          </div>
        )}
      </div>
    </div>
  )
}